/**
 * tailorMeasurementGuide.js — comprehensive per-category measurement schemas
 * for the tailor-side view. Goes BEYOND the customer schema by adding:
 *   · pro tailor labels ("Front Neck Depth" not "front_neck_depth")
 *   · "where to measure" guidance for beginner tailors
 *   · grouping (body / length / sleeve / detailing / allowance)
 *   · derived fields the tailor needs but customer didn't capture
 *
 * Stays in English — backend schema field-IDs are preserved in `id`.
 */

// ─── Group ordering (renders top-to-bottom in this order) ───────────────────
export const GROUP_ORDER = ['body', 'length', 'sleeve', 'detailing', 'allowance'];
export const GROUP_META = {
  body:       { label: 'Body Measurements',      tone: '#1A1A1A' },
  length:     { label: 'Length Measurements',    tone: '#1A1A1A' },
  sleeve:     { label: 'Sleeve & Armhole',       tone: '#1A1A1A' },
  detailing:  { label: 'Detailing & Shaping',    tone: '#7A6A4D' },
  allowance:  { label: 'Allowances',             tone: '#7A6A4D' },
};

// Tailor field schemas. Each entry:
//   { id, label, group, hint, derived?: (m) => number, defaultCm?: number, required? }
// `derived` runs when the customer didn't supply the field — gives the tailor
// a smart default to start from instead of an empty box.
const _round = (n) => (n == null ? null : Math.round(Number(n)));

const TAILOR_SCHEMAS = {
  // ─── BLOUSE — full pro spec from user requirements ────────────────────────
  blouse: [
    // Body
    { id: 'bust',             label: 'Bust',              group: 'body',  required: true,
      hint: 'Around fullest part of bust, tape parallel to floor.' },
    { id: 'upper_bust',       label: 'Upper Bust',        group: 'body',
      hint: 'Above bust, under armpits.',
      derived: m => _round(Number(m.bust) - 5) },
    { id: 'under_bust',       label: 'Under Bust',        group: 'body',
      hint: 'Just below the bust line.',
      derived: m => _round(Number(m.bust) - 10) },
    { id: 'waist',            label: 'Waist',             group: 'body',
      hint: 'Natural waistline (narrowest point).',
      derived: m => _round(Number(m.bust) - 15) },
    { id: 'shoulder',         label: 'Shoulder',          group: 'body',  required: true,
      hint: 'Tip-to-tip across the back.' },

    // Length
    { id: 'blouse_length',    label: 'Blouse Length',     group: 'length', required: true,
      hint: 'From shoulder seam down to desired hem.' },
    { id: 'side_length',      label: 'Side Length',       group: 'length',
      hint: 'From armhole bottom to hem.',
      derived: m => _round(Number(m.blouse_length) - 18) },
    { id: 'back_length',      label: 'Back Length',       group: 'length',
      hint: 'Back neck base to hem.',
      derived: m => _round(Number(m.blouse_length) + 1) },
    { id: 'princess_length',  label: 'Princess Length',   group: 'length',
      hint: 'Shoulder seam through bust apex to hem.',
      derived: m => _round(Number(m.blouse_length) + 4) },

    // Sleeve & Armhole
    { id: 'sleeve_length',    label: 'Sleeve Length',     group: 'sleeve', required: true,
      hint: 'Shoulder tip to sleeve hem.' },
    { id: 'sleeve_round',     label: 'Sleeve Round',      group: 'sleeve',
      hint: 'Around the sleeve at the bicep area.',
      derived: m => _round(Number(m.armhole) ? Number(m.armhole) - 5 : 30) },
    { id: 'armhole',          label: 'Armhole',           group: 'sleeve',
      hint: 'All the way around the armhole opening.',
      derived: m => _round(Number(m.bust) * 0.42) },

    // Detailing
    { id: 'front_neck_depth', label: 'Front Neck Depth',  group: 'detailing',
      hint: 'Shoulder line down to neckline front.', defaultCm: 14 },
    { id: 'back_neck_depth',  label: 'Back Neck Depth',   group: 'detailing',
      hint: 'Shoulder line down to back neckline.', defaultCm: 10 },
    { id: 'neck_width',       label: 'Neck Width',        group: 'detailing',
      hint: 'Across the front neck opening.', defaultCm: 16 },
    { id: 'apex_distance',    label: 'Apex Distance',     group: 'detailing',
      hint: 'Distance between two bust apex points.',
      derived: m => _round(Number(m.bust) * 0.20) },
    { id: 'darts',            label: 'Darts',             group: 'detailing',
      hint: 'Number / size of bust + waist darts.',
      defaultText: 'Standard (2 bust + 2 waist)' },

    // Allowance
    { id: 'cup_shaping',      label: 'Cup Shaping Allowance', group: 'allowance',
      hint: 'Extra ease for cup shape (pad/no-pad).', defaultCm: 1 },
    { id: 'margin_allowance', label: 'Margin Allowance',  group: 'allowance',
      hint: 'Seam-letting-out allowance for future alterations.', defaultCm: 2 },
  ],

  // ─── SUIT (Anarkali / Kurti / Salwar) ────────────────────────────────────
  suit: [
    { id: 'bust',          label: 'Bust',           group: 'body',  required: true,
      hint: 'Fullest part of bust.' },
    { id: 'waist',         label: 'Waist',          group: 'body',  required: true,
      hint: 'Natural waistline.' },
    { id: 'hip',           label: 'Hip',            group: 'body',  required: true,
      hint: 'Around fullest part of hips.' },
    { id: 'shoulder',      label: 'Shoulder',       group: 'body',  required: true,
      hint: 'Tip-to-tip.' },

    { id: 'kurti_length',  label: 'Kurti Length',   group: 'length', required: true,
      hint: 'Shoulder to kurti hem.' },
    { id: 'salwar_length', label: 'Salwar Length',  group: 'length', required: true,
      hint: 'Waist to ankle.' },
    { id: 'salwar_bottom', label: 'Salwar Bottom',  group: 'length',
      hint: 'Salwar hem opening width.' },

    { id: 'sleeve_length', label: 'Sleeve Length',  group: 'sleeve',
      hint: 'Shoulder tip to sleeve hem.' },
    { id: 'sleeve_round',  label: 'Sleeve Round',   group: 'sleeve',
      hint: 'Around the bicep area.',
      derived: m => _round(Number(m.bust) * 0.32) },
    { id: 'armhole',       label: 'Armhole',        group: 'sleeve',
      derived: m => _round(Number(m.bust) * 0.42) },

    { id: 'front_neck_depth', label: 'Front Neck Depth', group: 'detailing',
      hint: 'Shoulder to neckline front.', defaultCm: 18 },
    { id: 'back_neck_depth',  label: 'Back Neck Depth',  group: 'detailing',
      defaultCm: 12 },

    { id: 'margin_allowance', label: 'Margin Allowance', group: 'allowance', defaultCm: 2 },
  ],

  // ─── KURTA / KURTA-PAJAMA (Men) ──────────────────────────────────────────
  kurta_pajama: [
    { id: 'chest',         label: 'Chest',          group: 'body', required: true },
    { id: 'waist',         label: 'Waist',          group: 'body' },
    { id: 'shoulder',      label: 'Shoulder',       group: 'body', required: true,
      hint: 'Tip-to-tip across the back.' },
    { id: 'neck',          label: 'Neck',           group: 'body',
      hint: 'Around the base of neck (collar).' },

    { id: 'kurta_length',  label: 'Kurta Length',   group: 'length', required: true,
      hint: 'Shoulder to kurta hem.' },
    { id: 'pajama_length', label: 'Pajama Length',  group: 'length', required: true,
      hint: 'Waist to ankle.' },
    { id: 'pajama_bottom', label: 'Pajama Bottom',  group: 'length',
      hint: 'Pajama bottom opening width.' },

    { id: 'sleeve_length', label: 'Sleeve Length',  group: 'sleeve', required: true,
      hint: 'Shoulder tip to wrist.' },
    { id: 'sleeve_round',  label: 'Sleeve Round (Bicep)', group: 'sleeve',
      derived: m => _round(Number(m.chest) * 0.32) },
    { id: 'cuff',          label: 'Cuff',           group: 'sleeve',
      hint: 'Around the wrist with watch space.', defaultCm: 24 },
    { id: 'armhole',       label: 'Armhole',        group: 'sleeve',
      derived: m => _round(Number(m.chest) * 0.42) },

    { id: 'placket_length', label: 'Placket Length',  group: 'detailing',
      hint: 'Front opening length.', defaultCm: 22 },
    { id: 'side_slit',      label: 'Side Slit',       group: 'detailing',
      hint: 'Slit length from hem upward.', defaultCm: 18 },

    { id: 'margin_allowance', label: 'Margin Allowance', group: 'allowance', defaultCm: 2 },
  ],

  // ─── MEN'S SHIRT ─────────────────────────────────────────────────────────
  men_shirt: [
    { id: 'chest',         label: 'Chest',          group: 'body', required: true },
    { id: 'waist',         label: 'Waist',          group: 'body' },
    { id: 'shoulder',      label: 'Shoulder',       group: 'body', required: true },
    { id: 'neck',          label: 'Neck (Collar)',  group: 'body', required: true },

    { id: 'shirt_length',  label: 'Shirt Length',   group: 'length', required: true },

    { id: 'sleeve_length', label: 'Sleeve Length',  group: 'sleeve', required: true },
    { id: 'cuff',          label: 'Cuff',           group: 'sleeve',
      hint: 'Around wrist with watch space.', defaultCm: 24 },
    { id: 'armhole',       label: 'Armhole',        group: 'sleeve',
      derived: m => _round(Number(m.chest) * 0.42) },
    { id: 'bicep',         label: 'Bicep',          group: 'sleeve',
      derived: m => _round(Number(m.chest) * 0.32) },

    { id: 'collar_height', label: 'Collar Height',  group: 'detailing', defaultCm: 4 },
    { id: 'placket',       label: 'Placket Width',  group: 'detailing', defaultCm: 3.5 },

    { id: 'margin_allowance', label: 'Margin Allowance', group: 'allowance', defaultCm: 2 },
  ],

  // ─── TROUSER / PANT ──────────────────────────────────────────────────────
  trouser: [
    { id: 'waist',     label: 'Waist',     group: 'body', required: true },
    { id: 'hip',       label: 'Hip',       group: 'body', required: true },
    { id: 'thigh',     label: 'Thigh',     group: 'body',
      derived: m => _round(Number(m.hip) * 0.55) },
    { id: 'knee',      label: 'Knee',      group: 'body',
      derived: m => _round(Number(m.hip) * 0.36) },

    { id: 'inseam',    label: 'Inseam',    group: 'length', required: true,
      hint: 'Crotch to ankle, inside leg.' },
    { id: 'outseam',   label: 'Outseam',   group: 'length',
      hint: 'Waist to ankle, outside leg.' },
    { id: 'rise',      label: 'Rise',      group: 'length',
      hint: 'Crotch to top of waistband.' },
    { id: 'bottom_hem', label: 'Bottom Hem', group: 'length', required: true,
      hint: 'Bottom opening width (one side).' },

    { id: 'pocket_depth', label: 'Pocket Depth', group: 'detailing', defaultCm: 18 },
    { id: 'belt_loops',   label: 'Belt Loops',   group: 'detailing', defaultText: '5 standard' },

    { id: 'margin_allowance', label: 'Margin Allowance', group: 'allowance', defaultCm: 2 },
  ],

  // ─── SHERWANI / BLAZER ───────────────────────────────────────────────────
  sherwani: [
    { id: 'chest',     label: 'Chest',         group: 'body',  required: true },
    { id: 'waist',     label: 'Waist',         group: 'body' },
    { id: 'hip',       label: 'Hip',           group: 'body' },
    { id: 'shoulder',  label: 'Shoulder',      group: 'body',  required: true },
    { id: 'neck',      label: 'Neck (Stand-Collar)', group: 'body', required: true },

    { id: 'length',         label: 'Sherwani Length',  group: 'length', required: true,
      hint: 'Shoulder to hem (knee or below-knee).' },
    { id: 'churidar_length', label: 'Churidar Length', group: 'length',
      hint: 'Waist to ankle.' },

    { id: 'sleeve_length', label: 'Sleeve Length',  group: 'sleeve', required: true },
    { id: 'bicep',         label: 'Bicep',          group: 'sleeve',
      derived: m => _round(Number(m.chest) * 0.32) },
    { id: 'cuff',          label: 'Cuff',           group: 'sleeve', defaultCm: 26 },
    { id: 'armhole',       label: 'Armhole',        group: 'sleeve',
      derived: m => _round(Number(m.chest) * 0.42) },

    { id: 'collar_height', label: 'Stand-Collar Height', group: 'detailing', defaultCm: 5 },
    { id: 'placket_length', label: 'Placket Length', group: 'detailing',
      hint: 'Front button placket length.', defaultCm: 28 },
    { id: 'side_slit',     label: 'Side Slit',      group: 'detailing', defaultCm: 25 },

    { id: 'margin_allowance', label: 'Margin Allowance', group: 'allowance', defaultCm: 2 },
  ],

  // ─── LEHENGA (skirt + choli + dupatta) ───────────────────────────────────
  lehenga: [
    { id: 'bust',          label: 'Bust',           group: 'body',  required: true },
    { id: 'waist',         label: 'Waist',          group: 'body',  required: true },
    { id: 'hip',           label: 'Hip',            group: 'body',  required: true },
    { id: 'shoulder',      label: 'Shoulder',       group: 'body',  required: true },

    { id: 'lehenga_length', label: 'Lehenga Length', group: 'length', required: true,
      hint: 'Waist to floor.' },
    { id: 'choli_length',   label: 'Choli Length',   group: 'length', required: true,
      hint: 'Shoulder to choli hem (cropped).' },

    { id: 'sleeve_length', label: 'Sleeve Length',  group: 'sleeve' },
    { id: 'sleeve_round',  label: 'Sleeve Round',   group: 'sleeve',
      derived: m => _round(Number(m.bust) * 0.32) },
    { id: 'armhole',       label: 'Armhole',        group: 'sleeve',
      derived: m => _round(Number(m.bust) * 0.42) },

    { id: 'flare_panels',  label: 'Flare Panels',   group: 'detailing',
      hint: 'Number of kalis (panels) for skirt fullness.', defaultText: '8 kalis' },
    { id: 'kamarbandh',    label: 'Kamarbandh',     group: 'detailing',
      hint: 'Waist-belt width.', defaultCm: 5 },
    { id: 'front_neck_depth', label: 'Front Neck Depth', group: 'detailing', defaultCm: 16 },
    { id: 'back_neck_depth',  label: 'Back Neck Depth',  group: 'detailing', defaultCm: 12 },

    { id: 'margin_allowance', label: 'Margin Allowance', group: 'allowance', defaultCm: 2 },
  ],
};

// Map customer category → schema key
const CATEGORY_TO_SCHEMA = {
  blouse: 'blouse', saree_blouse: 'blouse', saree: 'blouse',
  suit: 'suit', salwar_suit: 'suit', anarkali: 'suit', kurti: 'suit',
  lehenga: 'lehenga', lehenga_choli: 'lehenga',
  kurta: 'kurta_pajama', kurta_pajama: 'kurta_pajama',
  shirt: 'men_shirt', men_shirt: 'men_shirt',
  trouser: 'trouser', pant: 'trouser', jeans: 'trouser',
  sherwani: 'sherwani', blazer: 'sherwani', suit_men: 'sherwani',
};

export const schemaKeyForCategory = (category) =>
  CATEGORY_TO_SCHEMA[(category || '').toLowerCase().trim()] || null;

/**
 * Build the complete tailor view of measurements for a given category.
 * Returns ordered fields with values + provenance:
 *   { id, label, group, hint, value, unit, source }
 * source ∈ 'scan_ease' | 'scan' | 'edited' | 'choice' | 'rule' | 'recorded' | 'derived' | 'default' | 'pending'
 */
// Engine provenance → tailor chip. Orders without provenance (legacy) are 'recorded' (source unknown).
const PROVENANCE_TO_CHIP = {
  scan_plus_ease: 'scan_ease', scan: 'scan', customer_edited: 'edited', style_choice: 'choice',
  style_default: 'default', fit_rule: 'rule', height_estimate: 'derived', size_estimate: 'derived',
  photo_estimate: 'derived', estimate: 'derived',
};

export const buildTailorView = (category, measurements = {}, provenance = null) => {
  const key = schemaKeyForCategory(category);
  if (!key || !TAILOR_SCHEMAS[key]) return [];
  const m = measurements || {};
  return TAILOR_SCHEMAS[key].map(f => {
    let value, source;
    if (m[f.id] != null && m[f.id] !== '') {
      value = m[f.id];
      source = provenance ? (PROVENANCE_TO_CHIP[provenance[f.id]] || 'recorded') : 'recorded';
    } else if (typeof f.derived === 'function') {
      const d = f.derived(m);
      value = (d == null || Number.isNaN(d)) ? null : d;
      source = value != null ? 'derived' : 'pending';
    } else if (f.defaultCm != null) {
      value = f.defaultCm;
      source = 'default';
    } else if (f.defaultText != null) {
      value = f.defaultText;
      source = 'default';
    } else {
      value = null;
      source = 'pending';
    }
    const isText = typeof value === 'string' && Number.isNaN(Number(value));
    return {
      id: f.id,
      label: f.label,
      group: f.group,
      hint: f.hint || '',
      required: !!f.required,
      value,
      unit: isText ? '' : 'cm',
      source,
    };
  });
};

export default TAILOR_SCHEMAS;
